import { Button, Avatar as MuiAvatar, Stack, Typography } from "@mui/material";
import { ConfirmDialog } from "Components/Shared/Dialogs/ConfirmDialog";
import { ProfilePhotoCropDialog } from "Components/Shared/ProfilePhoto/ProfilePhotoCropDialog";
import { useConfirmDialog } from "Hooks/useConfirmDialog";
import { useImageCrop } from "Hooks/useImageCrop";
import { useRef } from "react";
import { toast } from "sonner";
import { Resources, useResource } from "Translations/Resources";

const RESOURCES = Resources.Components.ProfilePhoto;
const ACCEPTED_TYPES = "image/png,image/jpeg,image/webp";
const ALLOWED_MIME_TYPES = ["image/png", "image/jpeg", "image/webp"];
const MAX_SIZE_BYTES = 2 * 1024 * 1024;
const PREVIEW_SIZE = 100;

type Props = {
  /** Current photo; undefined renders `fallback` and hides the remove action. */
  previewUrl: string | undefined;
  /** Shown inside the avatar when there is no photo (initials, a placeholder image, …). */
  fallback: React.ReactNode;
  /** Caption under the actions; callers vary it with the access state (formats, why locked, …). */
  hint?: string;
  removeConfirmMessage: string;
  isUploading: boolean;
  isDeleting: boolean;
  /** Receives the cropped image; the crop dialog closes once the returned promise resolves. */
  onUpload: (photo: Blob) => Promise<unknown>;
  onDelete: () => void;
  isReadOnly?: boolean;
};

/**
 * The one avatar editing flow — file picker, type/size validation, round crop with zoom,
 * remove with confirmation. Knows nothing about WHOSE photo it edits: the caller supplies the
 * preview, the fallback, the copy that differs per subject, and the mutations (incl. their
 * toasts). Used for the signed-in persona's own photo and for the back-office merchant avatar.
 */
export const ProfilePhotoEditor = ({
  previewUrl,
  fallback,
  hint,
  removeConfirmMessage,
  isUploading,
  isDeleting,
  onUpload,
  onDelete,
  isReadOnly = false,
}: Props) => {
  const { t } = useResource();
  const inputRef = useRef<HTMLInputElement>(null);
  const imageCrop = useImageCrop();
  const removeConfirmationDialog = useConfirmDialog();
  const isPending = isUploading || isDeleting;

  const handleFileChange = async (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file) {
      return;
    }

    if (!ALLOWED_MIME_TYPES.includes(file.type)) {
      toast.error(t(RESOURCES.InvalidFileType));
      return;
    }

    if (file.size > MAX_SIZE_BYTES) {
      toast.error(t(RESOURCES.FileTooLarge));
      return;
    }

    try {
      await imageCrop.open(file);
    } catch {
      toast.error(t(Resources.Error.SomethingWentWrong));
    }
  };

  const handleSaveCrop = async () => {
    let photo: Blob | null;
    try {
      photo = await imageCrop.getBlob();
    } catch {
      toast.error(t(Resources.Error.SomethingWentWrong));
      return;
    }
    if (!photo) {
      return;
    }

    try {
      await onUpload(photo);
      imageCrop.close();
    } catch {
      // The caller's mutation reports the failure; the dialog stays open for a retry.
    }
  };

  return (
    <>
      <Stack
        direction={["column", "row"]}
        gap={4}
        alignItems="center"
        flexWrap="wrap"
        pt={{ xs: 2, sm: 0 }}
      >
        <MuiAvatar
          src={previewUrl}
          sx={{
            width: PREVIEW_SIZE,
            height: PREVIEW_SIZE,
            fontSize: "2rem",
            bgcolor: "primary.main",
          }}
        >
          {previewUrl ? undefined : fallback}
        </MuiAvatar>
        <Stack gap={1} flex={1} minWidth={200}>
          {!isReadOnly && (
            <Stack
              direction="row"
              gap={2}
              flexWrap="wrap"
              justifyContent={["center", "flex-start"]}
            >
              <Button
                color="primary"
                size="small"
                variant="contained"
                onClick={() => inputRef.current?.click()}
                disabled={isPending}
                loading={isUploading}
              >
                {t(RESOURCES.ChangeAction)}
              </Button>
              {!!previewUrl && (
                <Button
                  color="error"
                  size="small"
                  variant="outlined"
                  onClick={removeConfirmationDialog.open}
                  disabled={isPending}
                  loading={isDeleting}
                >
                  {t(RESOURCES.RemoveAction)}
                </Button>
              )}
            </Stack>
          )}
          {hint && (
            <Typography variant="caption" color="text.secondary">
              {hint}
            </Typography>
          )}
        </Stack>
      </Stack>
      <input
        ref={inputRef}
        type="file"
        accept={ACCEPTED_TYPES}
        hidden
        onChange={handleFileChange}
      />
      <ProfilePhotoCropDialog
        imageSrc={imageCrop.imageSrc}
        crop={imageCrop.crop}
        zoom={imageCrop.zoom}
        isUploading={isUploading}
        isSaveDisabled={!imageCrop.croppedAreaPixels}
        onCropChange={imageCrop.setCrop}
        onZoomChange={imageCrop.setZoom}
        onCropComplete={imageCrop.setCroppedAreaPixels}
        onCancel={imageCrop.close}
        onSave={handleSaveCrop}
      />
      <ConfirmDialog
        isDestructive
        isOpen={removeConfirmationDialog.isOpen}
        modalTitle={t(RESOURCES.RemoveAction)}
        modalContent={<Typography>{removeConfirmMessage}</Typography>}
        handleResult={removeConfirmationDialog.handleResult(onDelete)}
      />
    </>
  );
};
